import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useMemo, useState } from 'react';

import { BottomSlidePanel } from '@components/BottomSlidePanel';
import { PositionCloseSuccessModal } from '@components/PositionCloseSuccessModal';
import { SlideToConfirmButton } from '@components/SlideToConfirmButton';
import { useAccount } from '@hooks/useAccount';
import type { AppPage } from '@hooks/useNavigation';
import { useOrders } from '@hooks/useOrders';
import type { AggregatedPosition } from '@hooks/usePositionAggregation';
import { useRoundTimer } from '@hooks/useRoundTimer';
import { orderService } from '@services/orderService';
import type { Position as AccountPosition, Asset } from '@shared/types';
import { brandColors } from '@utils/color-utils';

import { ClosePositionPanel } from './market/ClosePositionModal';
import { Header } from './shared/Header';

interface MyPositionsScreenProps {
  advancedMode: boolean;
  onNavigate: (page: AppPage) => void;
  animatePositionsPill?: boolean;
}

export const MyPositionsScreen = ({
  advancedMode: _advancedMode,
  onNavigate,
}: MyPositionsScreenProps) => {
  const { positions, getPositionPnLData } = useAccount();
  const { openOrders, cancelOrder } = useOrders();
  const [showCloseAllConfirm, setShowCloseAllConfirm] = useState(false);
  const [showCloseAllSuccess, setShowCloseAllSuccess] = useState(false);
  const [closeAllSummary, setCloseAllSummary] = useState<{
    totalPnL: number;
    positionCount: number;
    mixedTypes: boolean;
  } | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<Asset | 'ALL'>('ALL');
  const [selectedPosition, setSelectedPosition] =
    useState<AggregatedPosition | null>(null);
  const { formatTime } = useRoundTimer();

  const marketPageByAsset: Record<AccountPosition['asset'], AppPage> = {
    BTC: 'btcmarket',
    ETH: 'ethmarket',
    SOL: 'solmarket',
    // XRP: 'xrpmarket',
  };

  const navigateToMarket = (asset: AccountPosition['asset']) => {
    const page = marketPageByAsset[asset];
    onNavigate(page);
  };

  // Filter positions based on selected filter
  const filteredPositions = useMemo(() => {
    if (selectedFilter === 'ALL') {
      return positions;
    }
    return positions.filter(position => position.asset === selectedFilter);
  }, [positions, selectedFilter]);

  // Filter open orders based on selected filter
  const filteredOpenOrders = useMemo(() => {
    // Show all PENDING orders (both BUY and SELL)
    const pendingOrders = openOrders.filter(
      order => order.status === 'PENDING',
    );
    if (selectedFilter === 'ALL') {
      return pendingOrders;
    }
    return pendingOrders.filter(order => order.asset === selectedFilter);
  }, [openOrders, selectedFilter]);

  // Filter options
  const filterOptions: Array<{ label: string; value: Asset | 'ALL' }> = [
    { label: 'ALL', value: 'ALL' },
    { label: 'BTC', value: 'BTC' },
    { label: 'ETH', value: 'ETH' },
    { label: 'SOL', value: 'SOL' },
    // { label: 'XRP', value: 'XRP' },
  ];

  // Simple PnL calculation using only AccountService
  const getPositionPnL = (position: AccountPosition): number => {
    const pnlData = getPositionPnLData(position);
    return pnlData?.pnlTotal ?? 0;
  };

  // Check if a position has open sell orders
  const hasOpenSellOrder = useCallback(
    (positionId: string): boolean => {
      return openOrders.some(
        order =>
          order.side === 'SELL' &&
          order.status === 'PENDING' &&
          order.positionId === positionId,
      );
    },
    [openOrders],
  );

  // Filter positions that can be closed (no existing sell orders)
  const closablePositions = useMemo(() => {
    return filteredPositions.filter(position => !hasOpenSellOrder(position.id));
  }, [filteredPositions, hasOpenSellOrder]);

  // Calculate total PnL for filtered positions
  const filteredPositionsPnL = useMemo(() => {
    return filteredPositions.reduce((total, position) => {
      return total + getPositionPnL(position);
    }, 0);
  }, [filteredPositions, getPositionPnLData]);

  const handleCloseAll = () => {
    // Only close positions that don't have existing sell orders
    const positionsToClose = closablePositions;

    // Calculate summary before closing positions
    const positionCount = positionsToClose.length;
    const totalPnL = positionsToClose.reduce((total, position) => {
      return total + getPositionPnL(position);
    }, 0);
    const positionTypes = new Set(positionsToClose.map(p => p.type));
    const mixedTypes = positionTypes.size > 1;

    // Store summary for the success modal
    setCloseAllSummary({
      totalPnL,
      positionCount,
      mixedTypes,
    });

    // Close all closable positions
    positionsToClose.forEach(position => {
      orderService.createOrder({
        asset: position.asset,
        type: position.type,
        strikePrice: position.strikePrice,
        quantity: position.quantity,
        orderType: 'MARKET',
        side: 'SELL',
        expiryTimestamp: position.expiryTimestamp,
        positionId: position.id,
      });
    });

    setShowCloseAllConfirm(false);
    setShowCloseAllSuccess(true);
  };

  return (
    <div
      className="bg-brand-dark h-full w-full flex flex-col"
      style={{ backgroundColor: 'var(--brand-dark)' }}
    >
      {/* Shared Header - Fixed */}
      <Header asset="POSITIONS" title="MY POSITIONS" onNavigate={onNavigate} />

      {/* Filter Buttons - Fixed */}
      <div className="flex gap-3 w-full py-2 px-2 bg-brand-dark">
        {filterOptions.map(option => (
          <motion.button
            key={option.value}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedFilter === option.value
                ? 'bg-white/20 text-white'
                : 'bg-white/10 text-white/60 hover:bg-white/10 hover:text-white/80'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedFilter(option.value)}
          >
            {option.label}
          </motion.button>
        ))}
      </div>

      {/* Fixed Table Header - Only show when there are positions or open orders */}
      {(filteredPositions.length > 0 || filteredOpenOrders.length > 0) && (
        <div className="grid grid-cols-[1fr_.5fr_1fr_1.5fr] gap-2 items-center px-2 bg-brand-dark">
          <div className="px-2 py-3 text-[12px] text-white/40 bg-brand-dark/30 backdrop-blur font-normal">
            Strike Price
          </div>
          <div className="px-2 py-3 text-[12px] text-white/40 bg-brand-dark/30 backdrop-blur font-normal text-right">
            Qty
          </div>
          <div className="px-2 pr-4 py-3 text-[12px] text-white/40 bg-brand-dark/30 backdrop-blur font-normal text-right">
            Value
          </div>
          <div className="px-2 py-3 text-[12px] text-white/40 bg-brand-dark/30 backdrop-blur font-normal text-right">
            <span className="mr-2">Expires in</span>
            <span className="text-white font-bold">{formatTime()}</span>
          </div>
        </div>
      )}

      {/* Scrollable Positions Content */}
      <div className="flex-1 overflow-hidden relative">
        <div className="overflow-y-auto h-full pb-16">
          {filteredPositions.length === 0 && filteredOpenOrders.length === 0 ? (
            /* Empty State */
            <div className="flex items-center justify-center h-[200px] w-full">
              <div className="text-center">
                <div className="font-['Inter:Medium',_sans-serif] font-medium text-[#808080] text-[18px] mb-2">
                  {selectedFilter === 'ALL'
                    ? 'No positions yet'
                    : `No ${selectedFilter} positions`}
                </div>
                <div className="font-['Inter:Light',_sans-serif] font-light text-[#606060] text-[14px] mb-4">
                  {selectedFilter === 'ALL'
                    ? 'Start trading to see your positions here'
                    : `You don't have any ${selectedFilter} positions yet`}
                </div>
                <motion.button
                  className="border border-brand-green bg-brand-green/20 text-brand-green rounded-lg px-6 py-2 font-['Inter:Semi_Bold',_sans-serif] font-semibold text-[14px] hover:bg-brand-green/30 transition-colors"
                  onClick={() => onNavigate('ethmarket')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Start Trading
                </motion.button>
              </div>
            </div>
          ) : (
            <div className="px-2">
              <div className="grid grid-cols-[1fr_.5fr_1fr_1.5fr] gap-2 items-center">
                {/* Open Orders Section */}
                {filteredOpenOrders.length > 0 && (
                  <>
                    {/* Open Orders Header */}
                    <div className="col-span-4 mt-2 mb-1 px-2 py-1 bg-blue-400/10 border-l-2 border-blue-400">
                      <div className="text-blue-400 text-xs font-semibold tracking-wider">
                        PENDING ORDERS
                      </div>
                    </div>

                    {/* Open Order Rows */}
                    {filteredOpenOrders.map((order, index) => {
                      const formatNumber = (num: number) => {
                        return num.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                        });
                      };

                      // Use lighter blue color scheme matching the header
                      const isBuyOrder = order.side === 'BUY';
                      const bgColor = 'bg-blue-400/5';
                      const textColor = 'text-blue-400/80';
                      const borderColor = 'border-blue-400/50';
                      const buttonBg = 'bg-blue-400/10';
                      const buttonHoverBg = 'hover:bg-blue-400/20';

                      return (
                        <motion.div
                          key={order.id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.04 }}
                          className="contents"
                        >
                          {/* Strike Price with Arrow */}
                          <div
                            className={`flex items-center gap-2 pl-2 pr-1 py-1 ${bgColor}`}
                          >
                            <span
                              className="text-lg"
                              style={{
                                color:
                                  order.type === 'CALL'
                                    ? brandColors.green
                                    : brandColors.red,
                              }}
                            >
                              {order.type === 'CALL' ? '↑' : '↓'}
                            </span>
                            <span className="text-white/80 text-sm font-medium">
                              ${order.strikePrice.toLocaleString()}
                            </span>
                          </div>

                          {/* Quantity */}
                          <div
                            className={`text-white/80 text-sm text-center font-medium pl-2 pr-1 py-1 ${bgColor}`}
                          >
                            {isBuyOrder ? '' : '-'}
                            {formatNumber(order.quantity)}
                          </div>

                          {/* Order Type & Side */}
                          <div
                            className={`${textColor} text-xs text-right font-medium pl-2 pr-4 py-1 ${bgColor}`}
                          >
                            <span className="font-semibold">{order.side}</span>{' '}
                            {order.orderType}
                            {order.orderType === 'LIMIT' && order.limitPrice
                              ? ` @ $${formatNumber(order.limitPrice)}`
                              : ''}
                          </div>

                          {/* Cancel Button */}
                          <div
                            className={`flex justify-end pl-4 pr-1 py-1 ${bgColor}`}
                          >
                            <motion.button
                              className={`px-3 py-1.5 rounded-full border ${borderColor} ${buttonBg} ${textColor} text-sm font-medium ${buttonHoverBg} transition-colors`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => cancelOrder(order.id)}
                            >
                              Cancel
                            </motion.button>
                          </div>
                        </motion.div>
                      );
                    })}

                    {/* Divider if there are also positions */}
                    {filteredPositions.length > 0 && (
                      <div className="col-span-4 my-2 border-t border-white/10" />
                    )}
                  </>
                )}

                {/* Position Rows */}
                {filteredPositions.map(
                  (position: AccountPosition, index: number) => {
                    const pnl = getPositionPnL(position);
                    const pnlData = getPositionPnLData(position);
                    const hasOpenOrder = hasOpenSellOrder(position.id);
                    return (
                      <motion.div
                        key={position.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className="contents cursor-pointer"
                        onClick={() => navigateToMarket(position.asset)}
                      >
                        {/* Strike Price with Arrow */}
                        <div className="flex items-center gap-2 pl-2 pr-1 py-1">
                          <span
                            className="text-lg"
                            style={{
                              color:
                                position.type === 'CALL'
                                  ? brandColors.green
                                  : brandColors.red,
                            }}
                          >
                            {position.type === 'CALL' ? '↑' : '↓'}
                          </span>
                          <span className="text-white text-sm font-medium">
                            ${position.strikePrice.toLocaleString()}
                          </span>
                        </div>

                        {/* Quantity */}
                        <div className="text-white text-sm text-center font-medium pl-2 pr-1 py-1">
                          {position.quantity % 1 === 0
                            ? position.quantity.toString()
                            : position.quantity
                                .toFixed(2)
                                .replace(/\.?0+$/, '')}
                        </div>

                        {/* Value */}
                        <div className="text-white text-sm text-right font-medium pl-2 pr-4 py-1">
                          {pnlData
                            ? `$${pnlData.currentValue.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`
                            : '-'}
                        </div>

                        {/* PnL/Action Button - Only show if no open sell order */}
                        <div className="flex justify-end pl-4 pr-1 py-1">
                          {!hasOpenOrder && (
                            <motion.button
                              data-testid="position-pnl-button"
                              className={`flex items-center justify-between w-full gap-2 px-3 py-1.5 rounded-full border hover:scale-105 transition-transform ${
                                pnlData
                                  ? pnl >= 0
                                    ? 'border-brand-green/75 text-brand-green/75 bg-brand-green/15'
                                    : 'border-brand-red/75 text-brand-red/75 bg-brand-red/15'
                                  : 'border-white/75 text-white/75 bg-white/15'
                              }`}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={e => {
                                e.stopPropagation();

                                // Create an AggregatedPosition from the single position
                                const aggregatedPosition: AggregatedPosition = {
                                  id: `agg-${position.id}`,
                                  dominantType: position.type,
                                  positions: [position],
                                  breakEvenPrice: position.strikePrice,
                                  totalQuantity: position.quantity,
                                  totalPnL: pnl,
                                  callCount: position.type === 'CALL' ? 1 : 0,
                                  putCount: position.type === 'PUT' ? 1 : 0,
                                  averageStrike: position.strikePrice,
                                };

                                setSelectedPosition(aggregatedPosition);
                              }}
                            >
                              <span className="text-base font-bold text-center flex-1">
                                {pnlData
                                  ? `${pnl >= 0 ? '+' : '-'}$${Math.abs(
                                      pnl,
                                    ).toLocaleString('en-US', {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : '-'}
                              </span>
                              <span
                                className={`-mr-1 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  pnlData
                                    ? pnl >= 0
                                      ? 'bg-brand-green'
                                      : 'bg-brand-red'
                                    : 'bg-[#808080]'
                                }`}
                              >
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  className="text-black text-xs"
                                />
                              </span>
                            </motion.button>
                          )}
                        </div>
                      </motion.div>
                    );
                  },
                )}
              </div>
            </div>
          )}
        </div>

        {/* Overflow gradient indicator - only show when there are positions or open orders */}
        {(filteredPositions.length > 0 || filteredOpenOrders.length > 0) && (
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none" />
        )}
      </div>

      {/* Footer Close All Button - Only show when closable positions exist */}
      {closablePositions.length > 0 && (
        <div className="flex-shrink-0 p-4 bg-brand-dark/30 backdrop-blur-md border-t border-white/20">
          <motion.button
            className={`w-full rounded-lg px-4 py-3 font-['Inter:Semi_Bold',_sans-serif] font-semibold text-base border transition-colors ${
              filteredPositionsPnL >= 0
                ? 'bg-brand-green/20 border-brand-green text-brand-green hover:bg-brand-green/30'
                : 'bg-brand-red/20 border-brand-red text-brand-red hover:bg-brand-red/30'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCloseAllConfirm(true)}
          >
            {selectedFilter === 'ALL'
              ? 'Close All'
              : `Close All ${selectedFilter}`}{' '}
            ({closablePositions.length})
          </motion.button>
        </div>
      )}

      {/* Close All Confirmation Modal */}
      <BottomSlidePanel
        isOpen={showCloseAllConfirm}
        onClose={() => setShowCloseAllConfirm(false)}
        fullWidth={true}
        className="p-4 flex flex-col"
      >
        <div className="text-white text-[18px] font-semibold mb-3">
          {selectedFilter === 'ALL'
            ? 'Close All?'
            : `Close All ${selectedFilter}?`}
        </div>
        <div className="text-white/70 text-[14px] mb-8">
          This will sell {closablePositions.length}{' '}
          {selectedFilter === 'ALL'
            ? 'open position'
            : selectedFilter + ' position'}
          {closablePositions.length === 1 ? '' : 's'} at current market value
          {filteredPositions.length !== closablePositions.length && (
            <>
              {' '}
              ({filteredPositions.length - closablePositions.length} position
              {filteredPositions.length - closablePositions.length === 1
                ? ''
                : 's'}{' '}
              with existing sell orders will be skipped)
            </>
          )}
          . This action cannot be undone.
        </div>

        <div className="mt-auto">
          <SlideToConfirmButton
            onConfirm={handleCloseAll}
            className={`w-full ${
              filteredPositionsPnL >= 0 ? 'bg-green-600' : 'bg-red-500/80'
            }`}
            confirmText="RELEASE TO CONFIRM"
          >
            SLIDE TO{' '}
            {selectedFilter === 'ALL'
              ? 'CLOSE ALL'
              : `CLOSE ALL ${selectedFilter}`}
          </SlideToConfirmButton>
        </div>
      </BottomSlidePanel>

      {/* Close All Success Modal */}
      {closeAllSummary && (
        <PositionCloseSuccessModal
          isOpen={showCloseAllSuccess}
          onClose={() => {
            setShowCloseAllSuccess(false);
            setCloseAllSummary(null);
          }}
          realizedPnL={closeAllSummary.totalPnL}
          positionCount={closeAllSummary.positionCount}
          isExpiry={false}
        />
      )}

      {/* Close Position Panel */}
      <AnimatePresence>
        {selectedPosition && (
          <ClosePositionPanel
            isVisible={true}
            onClose={() => setSelectedPosition(null)}
            positionGroup={selectedPosition}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
